import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import {
  AcademicCapIcon,
  BookOpenIcon,
  ClockIcon,
  TrophyIcon,
  ChartBarIcon,
  PlayIcon,
  CheckCircleIcon,
  StarIcon,
  CalendarDaysIcon,
  UserGroupIcon,
  DocumentTextIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';

const TrainingDashboard = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);

  // Mock data - in real app this would come from API
  const trainingStats = {
    coursesCompleted: 15,
    hoursLearned: 42,
    certificatesEarned: 8,
    skillsGained: 12,
    currentStreak: 7,
    nextDeadline: '2025-01-25'
  };

  const currentCourses = [
    {
      id: 1,
      title: 'Advanced Healthcare Management',
      instructor: 'Dr. Sarah Wilson',
      progress: 65,
      duration: '8 weeks',
      nextLesson: 'Healthcare Quality Metrics',
      dueDate: '2025-01-25',
      category: 'Management',
      difficulty: 'Advanced',
      rating: 4.8,
      image: '/images/course-healthcare-mgmt.jpg'
    },
    {
      id: 2,
      title: 'Medical Ethics and Patient Care',
      instructor: 'Prof. Michael Chen',
      progress: 30,
      duration: '6 weeks',
      nextLesson: 'Informed Consent Procedures',
      dueDate: '2025-02-01',
      category: 'Ethics',
      difficulty: 'Intermediate',
      rating: 4.9,
      image: '/images/course-medical-ethics.jpg'
    },
    {
      id: 3,
      title: 'Emergency Response Protocols',
      instructor: 'Dr. Lisa Rodriguez',
      progress: 85,
      duration: '4 weeks',
      nextLesson: 'Crisis Communication',
      dueDate: '2025-01-30',
      category: 'Emergency',
      difficulty: 'Advanced',
      rating: 4.7,
      image: '/images/course-emergency.jpg'
    }
  ];

  const upcomingDeadlines = [
    {
      id: 1,
      course: 'Advanced Healthcare Management',
      assignment: 'Module 4 Assessment',
      dueDate: '2025-01-25T14:00:00',
      priority: 'high'
    },
    {
      id: 2,
      course: 'Emergency Response Protocols',
      assignment: 'Final Certification Exam',
      dueDate: '2025-01-30T10:00:00',
      priority: 'high'
    },
    {
      id: 3,
      course: 'Medical Ethics and Patient Care',
      assignment: 'Case Study Analysis',
      dueDate: '2025-02-01T16:00:00',
      priority: 'medium'
    }
  ];

  const recommendedCourses = [
    {
      id: 4,
      title: 'Digital Health Technologies',
      instructor: 'Dr. James Park',
      duration: '5 weeks',
      rating: 4.6,
      students: 1247,
      category: 'Technology',
      difficulty: 'Beginner',
      description: 'Learn about the latest digital health innovations and their implementation.',
      image: '/images/course-digital-health.jpg'
    },
    {
      id: 5,
      title: 'Healthcare Data Analytics',
      instructor: 'Prof. Anna Kumar',
      duration: '7 weeks',
      rating: 4.8,
      students: 892,
      category: 'Analytics',
      difficulty: 'Intermediate',
      description: 'Master data analysis techniques for healthcare decision-making.',
      image: '/images/course-data-analytics.jpg'
    },
    {
      id: 6,
      title: 'Patient Communication Skills',
      instructor: 'Dr. Emily Thompson',
      duration: '3 weeks',
      rating: 4.9,
      students: 2156,
      category: 'Communication',
      difficulty: 'Beginner',
      description: 'Enhance your patient interaction and communication abilities.',
      image: '/images/course-communication.jpg'
    }
  ];

  const achievements = [
    {
      id: 1,
      title: 'Course Completion Champion',
      description: 'Completed 15 courses this year',
      icon: TrophyIcon,
      color: 'bg-yellow-500',
      earned: true,
      date: '2025-01-15'
    },
    {
      id: 2,
      title: 'Learning Streak Master',
      description: '7 days continuous learning',
      icon: StarIcon,
      color: 'bg-purple-500',
      earned: true,
      date: '2025-01-20'
    },
    {
      id: 3,
      title: 'Skills Specialist',
      description: 'Master 5 different skill areas',
      icon: AcademicCapIcon,
      color: 'bg-blue-500',
      earned: false,
      progress: 80
    }
  ];

  useEffect(() => {
    // Simulate loading
    setTimeout(() => setIsLoading(false), 1000);
  }, []);

  const formatDueDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = date.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Due today';
    if (diffDays === 1) return 'Due tomorrow';
    if (diffDays > 0) return `Due in ${diffDays} days`;
    return `Overdue by ${Math.abs(diffDays)} days`;
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800 border-red-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Beginner': return 'bg-green-100 text-green-800';
      case 'Intermediate': return 'bg-yellow-100 text-yellow-800';
      case 'Advanced': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-apollo-accent"></div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <AcademicCapIcon className="h-8 w-8 text-apollo-accent mr-3" />
                Training Dashboard
              </h1>
              <p className="mt-2 text-gray-600">
                Track your progress, discover new courses, and advance your skills
              </p>
            </div>
            <button
              onClick={() => navigate('/training/catalog')}
              className="bg-apollo-accent text-white px-6 py-3 rounded-lg hover:bg-apollo-accent/90 transition-colors duration-200 flex items-center"
            >
              <BookOpenIcon className="h-5 w-5 mr-2" />
              Browse Catalog
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Courses Completed</p>
                <p className="text-3xl font-bold text-gray-900">{trainingStats.coursesCompleted}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-lg">
                <CheckCircleIcon className="h-6 w-6 text-green-600" />
              </div>
            </div>
            <div className="mt-4 flex items-center text-sm">
              <span className="text-green-600 flex items-center">
                <ArrowRightIcon className="h-4 w-4 mr-1" />
                +3 this month
              </span>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Hours Learned</p>
                <p className="text-3xl font-bold text-gray-900">{trainingStats.hoursLearned}h</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <ClockIcon className="h-6 w-6 text-blue-600" />
              </div>
            </div>
            <div className="mt-4 flex items-center text-sm">
              <span className="text-blue-600">
                12h this week
              </span>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Certificates</p>
                <p className="text-3xl font-bold text-gray-900">{trainingStats.certificatesEarned}</p>
              </div>
              <div className="p-3 bg-yellow-100 rounded-lg">
                <TrophyIcon className="h-6 w-6 text-yellow-600" />
              </div>
            </div>
            <div className="mt-4 flex items-center text-sm">
              <span className="text-yellow-600">
                2 pending
              </span>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Skills Gained</p>
                <p className="text-3xl font-bold text-gray-900">{trainingStats.skillsGained}</p>
              </div>
              <div className="p-3 bg-purple-100 rounded-lg">
                <ChartBarIcon className="h-6 w-6 text-purple-600" />
              </div>
            </div>
            <div className="mt-4 flex items-center text-sm">
              <span className="text-purple-600">
                {trainingStats.currentStreak} day streak
              </span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            {/* Current Courses */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100">
              <div className="p-6 border-b border-gray-100">
                <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                  <PlayIcon className="h-5 w-5 text-apollo-accent mr-2" />
                  Current Courses
                </h2>
                <p className="text-gray-600 mt-1">Continue your learning journey</p>
              </div>
              
              <div className="p-6">
                <div className="space-y-6">
                  {currentCourses.map((course) => (
                    <div key={course.id} className="flex items-center space-x-4 p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow duration-200">
                      <div className="w-16 h-16 bg-gradient-to-br from-apollo-accent to-blue-600 rounded-lg flex items-center justify-center">
                        <BookOpenIcon className="h-8 w-8 text-white" />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-lg font-medium text-gray-900 truncate">
                            {course.title}
                          </h3>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(course.difficulty)}`}>
                            {course.difficulty}
                          </span>
                        </div>
                        
                        <p className="text-sm text-gray-600 mb-2">
                          by {course.instructor} • {course.duration}
                        </p>
                        
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex-1">
                            <div className="flex items-center justify-between text-sm mb-1">
                              <span className="text-gray-600">Progress</span>
                              <span className="font-medium text-gray-900">{course.progress}%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div 
                                className="bg-apollo-accent h-2 rounded-full transition-all duration-300"
                                style={{ width: `${course.progress}%` }}
                              ></div>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">
                            Next: <span className="font-medium">{course.nextLesson}</span>
                          </span>
                          <span className="text-apollo-accent font-medium">
                            Due {formatDueDate(course.dueDate)}
                          </span>
                        </div>
                      </div>
                      
                      <button
                        onClick={() => navigate(`/training/course/${course.id}`)}
                        className="bg-apollo-accent text-white px-4 py-2 rounded-lg hover:bg-apollo-accent/90 transition-colors duration-200 flex items-center"
                      >
                        Continue
                        <ArrowRightIcon className="h-4 w-4 ml-1" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Recommended Courses */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100">
              <div className="p-6 border-b border-gray-100">
                <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                  <StarIcon className="h-5 w-5 text-apollo-accent mr-2" />
                  Recommended for You
                </h2>
                <p className="text-gray-600 mt-1">Based on your interests and career path</p>
              </div>
              
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {recommendedCourses.map((course) => (
                    <div key={course.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow duration-200">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h3 className="text-lg font-medium text-gray-900 mb-1">
                            {course.title}
                          </h3>
                          <p className="text-sm text-gray-600 mb-2">
                            by {course.instructor}
                          </p>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(course.difficulty)}`}>
                          {course.difficulty}
                        </span>
                      </div>
                      
                      <p className="text-sm text-gray-600 mb-4">
                        {course.description}
                      </p>
                      
                      <div className="flex items-center justify-between text-sm text-gray-600 mb-4">
                        <span className="flex items-center">
                          <ClockIcon className="h-4 w-4 mr-1" />
                          {course.duration}
                        </span>
                        <span className="flex items-center">
                          <UserGroupIcon className="h-4 w-4 mr-1" />
                          {course.students.toLocaleString()} students
                        </span>
                        <span className="flex items-center">
                          <StarIcon className="h-4 w-4 mr-1 text-yellow-400" />
                          {course.rating}
                        </span>
                      </div>
                      
                      <button
                        onClick={() => navigate(`/training/course/${course.id}`)}
                        className="w-full bg-gray-50 text-gray-900 px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors duration-200 border border-gray-200"
                      >
                        Learn More
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Upcoming Deadlines */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100">
              <div className="p-6 border-b border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <CalendarDaysIcon className="h-5 w-5 text-apollo-accent mr-2" />
                  Upcoming Deadlines
                </h3>
              </div>
              
              <div className="p-6">
                <div className="space-y-4">
                  {upcomingDeadlines.map((deadline) => (
                    <div key={deadline.id} className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-apollo-accent rounded-full mt-2"></div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">
                          {deadline.assignment}
                        </p>
                        <p className="text-sm text-gray-600">
                          {deadline.course}
                        </p>
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-xs text-gray-500">
                            {formatDueDate(deadline.dueDate)}
                          </span>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getPriorityColor(deadline.priority)}`}>
                            {deadline.priority}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Achievements */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100">
              <div className="p-6 border-b border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <TrophyIcon className="h-5 w-5 text-apollo-accent mr-2" />
                  Recent Achievements
                </h3>
              </div>
              
              <div className="p-6">
                <div className="space-y-4">
                  {achievements.map((achievement) => (
                    <div key={achievement.id} className="flex items-center space-x-3">
                      <div className={`p-2 rounded-lg ${achievement.color} ${achievement.earned ? '' : 'opacity-50'}`}>
                        <achievement.icon className="h-5 w-5 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium ${achievement.earned ? 'text-gray-900' : 'text-gray-500'}`}>
                          {achievement.title}
                        </p>
                        <p className={`text-xs ${achievement.earned ? 'text-gray-600' : 'text-gray-400'}`}>
                          {achievement.description}
                        </p>
                        {!achievement.earned && achievement.progress && (
                          <div className="mt-2">
                            <div className="w-full bg-gray-200 rounded-full h-1">
                              <div 
                                className="bg-apollo-accent h-1 rounded-full"
                                style={{ width: `${achievement.progress}%` }}
                              ></div>
                            </div>
                            <span className="text-xs text-gray-500">{achievement.progress}% complete</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100">
              <div className="p-6 border-b border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900">Quick Actions</h3>
              </div>
              
              <div className="p-6">
                <div className="space-y-3">
                  <button
                    onClick={() => navigate('/training/catalog')}
                    className="w-full text-left p-3 rounded-lg hover:bg-gray-50 transition-colors duration-200 flex items-center"
                  >
                    <BookOpenIcon className="h-5 w-5 text-apollo-accent mr-3" />
                    <span className="text-sm font-medium text-gray-900">Browse Course Catalog</span>
                  </button>
                  
                  <button
                    onClick={() => navigate('/training/my-training')}
                    className="w-full text-left p-3 rounded-lg hover:bg-gray-50 transition-colors duration-200 flex items-center"
                  >
                    <AcademicCapIcon className="h-5 w-5 text-apollo-accent mr-3" />
                    <span className="text-sm font-medium text-gray-900">My Training History</span>
                  </button>
                  
                  <button
                    onClick={() => navigate('/training/certificates')}
                    className="w-full text-left p-3 rounded-lg hover:bg-gray-50 transition-colors duration-200 flex items-center"
                  >
                    <TrophyIcon className="h-5 w-5 text-apollo-accent mr-3" />
                    <span className="text-sm font-medium text-gray-900">View Certificates</span>
                  </button>
                  
                  <button
                    onClick={() => navigate('/training/assessments')}
                    className="w-full text-left p-3 rounded-lg hover:bg-gray-50 transition-colors duration-200 flex items-center"
                  >
                    <DocumentTextIcon className="h-5 w-5 text-apollo-accent mr-3" />
                    <span className="text-sm font-medium text-gray-900">Skill Assessments</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default TrainingDashboard;
